import {
    Avatar,
    Divider,
    Heading,
    HStack,
    VStack,
    Text,
    Spinner,
} from '@chakra-ui/react'
import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

interface Contributor {
    user_id: string
    name: string
    last_name: string
    verses_contributed: number
}

interface EncodingHistoryEntry {
    text_id: string
    book_name: string
    chapter: number
    verse: number
    user_id: string
    first_name: string
    last_name: string
    encoding_history_id: string
    encoding_id: string
    created_at: string
}

const ContributorsContainer = styled(VStack)`
    width: 100%;
`

const ChapterContributors: React.FC<{
    textId: string
    bookName: string
    chapter: number
}> = ({ textId, bookName, chapter }) => {
    const [contributors, setContributors] = useState<Contributor[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const loadContributors = async () => {
            setLoading(true)
            try {
                const { data, error } = await supabase
                    .from('v_user_verse_contributions') // <— use the aggregated view name
                    .select(
                        'user_id, first_name, last_name, verses_contributed'
                    )
                    .eq('text_id', textId)
                    .eq('book_name', bookName)
                    .eq('chapter', chapter)
                    .order('verses_contributed', { ascending: false })

                if (error) {
                    console.error('Error loading contributors:', error)
                    setContributors([])
                    return
                }

                setContributors(
                    (data ?? []).map((row) => ({
                        user_id: row.user_id,
                        name: row.first_name ?? '',
                        last_name: row.last_name ?? '',
                        verses_contributed: row.verses_contributed ?? 0,
                    }))
                )
            } catch (err) {
                console.error('Error loading contributors:', err)
                setContributors([])
            } finally {
                setLoading(false)
            }
        }

        if (textId && bookName && chapter != null) loadContributors()
    }, [textId, bookName, chapter])

    if (loading) {
        return (
            <ContributorsContainer
                display={{
                    base: 'none',
                    md: 'flex',
                }}
                background="white"
                padding="24px"
                borderRadius="24px"
                maxWidth={'238px'}
                minHeight={'150px'}
                justifyContent="center"
            >
                <Spinner size="sm" color="teal.500" />
            </ContributorsContainer>
        )
    }

    if (contributors.length === 0) {
        return null
    }

    return (
        <ContributorsContainer
            display={{
                base: 'none',
                md: 'flex',
            }}
            background="white"
            padding="24px"
            borderRadius="24px"
            maxWidth={'238px'}
        >
            <Heading
                color={'twitter.800'}
                fontSize="2xl"
                textAlign="left"
                w="100%"
            >
                Contributors
            </Heading>
            <Divider />
            <VStack spacing={3} w="100%" alignItems="flex-start">
                {contributors.map((contributor) => (
                    <HStack
                        key={contributor.user_id}
                        spacing={3}
                        w="100%"
                        alignItems="center"
                    >
                        <Avatar
                            size="sm"
                            name={`${contributor.name} ${contributor.last_name}`}
                            bg="white"
                            borderColor="pink"
                            border="1px"
                            color="pink"
                        />
                        <VStack spacing={0} alignItems="flex-start" flex={1}>
                            <Text fontSize="sm" fontWeight="500">
                                {contributor.name} {contributor.last_name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                                {contributor.verses_contributed}{' '}
                                {contributor.verses_contributed === 1
                                    ? 'encoding'
                                    : 'encodings'}
                            </Text>
                        </VStack>
                    </HStack>
                ))}
                <Divider />
                <HStack spacing={3} w="100%" alignItems="center">
                    <Avatar
                        size="sm"
                        name="Yusuf Çelik"
                        bg="white"
                        borderColor="pink"
                        border="1px"
                        color="pink"
                    />
                    <VStack spacing={0} alignItems="flex-start" flex={1}>
                        <Text fontSize="sm" fontWeight="500">
                            Yusuf Çelik
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                            Data
                        </Text>
                    </VStack>
                </HStack>
                <HStack spacing={3} w="100%" alignItems="center">
                    <Avatar
                        size="sm"
                        name="Martijn Naaijer"
                        bg="white"
                        borderColor="pink"
                        border="1px"
                        color="pink"
                    />
                    <VStack spacing={0} alignItems="flex-start" flex={1}>
                        <Text fontSize="sm" fontWeight="500">
                            Martijn Naaijer
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                            Data
                        </Text>
                    </VStack>
                </HStack>
                <HStack spacing={3} w="100%" alignItems="center">
                    <Avatar
                        size="sm"
                        name="Constantijn Sikkel"
                        bg="white"
                        borderColor="pink"
                        border="1px"
                        color="pink"
                    />
                    <VStack spacing={0} alignItems="flex-start" flex={1}>
                        <Text fontSize="sm" fontWeight="500">
                            Constantijn Sikkel
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                            Data
                        </Text>
                    </VStack>
                </HStack>
            </VStack>
        </ContributorsContainer>
    )
}

export default ChapterContributors
